import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, TruckIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '../../../utils/formatting';

/**
 * PayrollDetailModal Component
 * 
 * Modal for viewing detailed payroll entry information
 */
const PayrollDetailModal = ({ isOpen, onClose, entry }) => {
  const { t } = useTranslation();

  if (!isOpen || !entry) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payroll.payroll_details', 'Payroll Details')} - {entry.employeeName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Employee Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('payroll.employee_info', 'Employee Information')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.employee_name', 'Employee Name')}
                  </label>
                  <div className="mt-1 text-sm text-gray-900">{entry.employeeName}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.employee_id', 'Employee ID')}
                  </label>
                  <div className="mt-1 text-sm text-gray-900">{entry.employeeId}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.role', 'Role')}
                  </label>
                  <div className="mt-1 text-sm text-gray-900">{entry.employeeRole}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.type', 'Type')}
                  </label>
                  <div className="mt-1">
                    <span className={`badge ${entry.employeeType === 'driver' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {entry.employeeType === 'driver' ? t('payroll.driver', 'Driver') : t('payroll.employee', 'Employee')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.period', 'Period')}
                  </label>
                  <div className="mt-1 text-sm text-gray-900">{entry.periodId}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('payroll.payment_status', 'Payment Status')}
                  </label>
                  <div className="mt-1">
                    <span className={`badge ${
                      entry.paymentStatus === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {entry.paymentStatus === 'paid' 
                        ? t('accounting.filter.paid')
                        : t('payroll.status.accrued')
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payroll Breakdown */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('payroll.payroll_breakdown', 'Payroll Breakdown')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">{t('payroll.salary', 'Salary')}</span>
                    <span className="font-medium">{formatCurrency(entry.salary || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">{t('payroll.commission', 'Commission')}</span>
                    <span className="font-medium">{formatCurrency(entry.commission || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">{t('payroll.bonus', 'Bonus')}</span>
                    <span className="font-medium">{formatCurrency(entry.bonus || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">{t('payroll.deductions', 'Deductions')}</span>
                    <span className="font-medium text-red-600">-{formatCurrency(entry.deductions || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-t-2 border-gray-300">
                    <span className="text-lg font-medium text-gray-900">{t('payroll.total_due', 'Total Due')}</span>
                    <span className="text-xl font-bold text-green-600">{formatCurrency(entry.totalDue || 0)}</span>
                  </div>
                </div>

                {/* Payment Information */}
                {entry.paymentStatus === 'paid' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-3">
                      {t('chart.payment_info')}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-green-700">{t('payroll.payment_date', 'Payment Date')}:</span>
                        <span className="font-medium">{formatDate(entry.paymentDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-700">{t('payroll.payment_method', 'Payment Method')}:</span>
                        <span className="font-medium">{entry.paymentMethod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-700">{t('payroll.payment_reference', 'Payment Reference')}:</span>
                        <span className="font-medium">{entry.paymentReference}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Commission Details */}
            {entry.commissionDetails && entry.commissionDetails.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TruckIcon className="w-5 h-5 mr-2 text-blue-600" />
                  {t('payroll.commission_details', 'Commission Details')}
                </h3>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('shipments.tracking_number', 'Tracking Number')}</th>
                        <th>{t('chart.trip_number')}</th>
                        <th>{t('shipments.route', 'Route')}</th>
                        <th>{t('shipments.delivery_date', 'Delivery Date')}</th>
                        <th>{t('payroll.commission_amount', 'Commission Amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.commissionDetails.map((detail, index) => (
                        <tr key={index}>
                          <td className="font-medium">{detail.trackingNumber}</td>
                          <td>{detail.tripNumber}</td>
                          <td>{detail.origin} → {detail.destination}</td>
                          <td>{formatDate(detail.deliveryDate)}</td>
                          <td className="font-medium text-green-600">{formatCurrency(detail.commissionAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td colSpan="4" className="font-medium text-right">
                          {t('payroll.total_commission', 'Total Commission')}:
                        </td>
                        <td className="font-bold text-green-600">
                          {formatCurrency(entry.commissionDetails.reduce((sum, detail) => sum + detail.commissionAmount, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Audit Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 text-gray-600" />
                {t('payroll.audit_info', 'Audit Information')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('common.created_at', 'Created At')}
                  </label>
                  <div className="mt-1 text-gray-900">
                    {entry.createdAt ? formatDate(entry.createdAt.toDate ? entry.createdAt.toDate() : entry.createdAt) : '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('common.created_by', 'Created By')}
                  </label>
                  <div className="mt-1 text-gray-900">{entry.createdBy || '-'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('common.updated_at', 'Updated At')}
                  </label>
                  <div className="mt-1 text-gray-900">
                    {entry.updatedAt ? formatDate(entry.updatedAt.toDate ? entry.updatedAt.toDate() : entry.updatedAt) : '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    {t('common.updated_by', 'Updated By')}
                  </label>
                  <div className="mt-1 text-gray-900">{entry.updatedBy || '-'}</div>
                </div>
                {entry.paymentStatus === 'paid' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        {t('payroll.paid_at', 'Paid At')}
                      </label>
                      <div className="mt-1 text-gray-900">
                        {entry.paidAt ? formatDate(entry.paidAt.toDate ? entry.paidAt.toDate() : entry.paidAt) : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        {t('payroll.paid_by', 'Paid By')}
                      </label>
                      <div className="mt-1 text-gray-900">{entry.paidBy || '-'}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            {t('common.close', t('common.close'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollDetailModal;